import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthShell, Field } from "@/pages/Login";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") || "");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState(params.get("phone") || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(name, email, password, phone);
      if (u.balance > 0) {
        toast.success(u?.balance > 0 ? `Account created! 🎁 $${u.balance.toFixed(2)} free credit added — try your first call.` : "Account created! Add credit to make your first call.");
        navigate("/app");
      } else {
        toast.success("Account created! Add credit to start calling.");
        navigate("/wallet");
      }
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start calling worldwide in minutes">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <Field label="Full name" value={name} onChange={setName} testid="register-name" placeholder="Ada Obi" />
        <Field label="Email" type="email" value={email} onChange={setEmail} testid="register-email" placeholder="you@example.com" />
        <Field label="Password" type="password" value={password} onChange={setPassword} testid="register-password" placeholder="At least 6 characters" />
        <div className="opacity-70">
          <Field label="Phone number — optional, you can skip this" type="tel" value={phone} onChange={setPhone} testid="register-phone" placeholder="+234 801 234 5678" />
          <p className="text-xs text-zinc-400 mt-1.5">Tip: add your number for callback calls — the most reliable quality when your internet is unstable. You can always call from your device without one.</p>
        </div>
        {error && <p className="text-sm text-destructive font-medium" data-testid="register-error">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="w-full h-12 rounded-xl bg-primary text-white font-semibold hover:bg-[#1E40AF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Create account
        </button>
        <p className="text-[11px] text-zinc-400 text-center">
          By creating an account you agree to our{" "}
          <Link to="/terms" className="text-primary hover:underline" data-testid="register-terms-link">Terms of Service</Link> and{" "}
          <Link to="/privacy" className="text-primary hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
        </p>
      </form>
      <p className="text-sm text-zinc-500 text-center mt-6">
        Already have an account?{" "}
        <Link to="/login" className="text-primary font-semibold" data-testid="goto-login">
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}
